import axios from "axios";
import { sessionOperations } from "state/ducks/session";
import { store } from "state/store";

const API = process.env.REACT_APP_LEMOULIN_API_URL;
const API_KEY = process.env.REACT_APP_LEMOULIN_API_KEY;
const APP_ENV = process.env.REACT_APP_ENV;
const BASIC_ID = process.env.REACT_APP_LEMOULIN_BASIC_ID;
const BASIC_SECRET = process.env.REACT_APP_LEMOULIN_BASIC_SECRET;

/** Jeton courant : celui du store en priorité, comme state/utils/fetch.js. */
const currentToken = () =>
  store.getState().session.token || window.localStorage.getItem("token");

/**
 * En-têtes d'auth identiques au wrapper redux : le JWT passe par
 * `X-Custom-Authorization`, seul en-tête lu par lexik (`token_extractors`).
 * Un `Authorization: Bearer` est ignoré (« JWT Token not found ») : sans
 * conséquence tant que ces routes étaient publiques, mais 401 systématique
 * depuis que /api/v2/admin exige un JWT admin (API, 16/09/2026).
 * `Authorization` reste réservé au Basic de la recette.
 */
const authHeaders = (token) => {
  const headers = {};
  if (APP_ENV === "staging") {
    headers.Authorization = `Basic ${btoa(`${BASIC_ID}:${BASIC_SECRET}`)}`;
  }
  if (token) {
    headers["X-Custom-Authorization"] = `Bearer ${token}`;
  }
  return headers;
};

let pendingRefresh = null;

/**
 * Rafraîchit le JWT (même appel que middlewares/apiService.js). Partagé par les
 * appels hors redux (cf. Stocks/CartePreview/api.js) : deux 401 simultanés
 * attendent le même refresh, le refresh token n'est consommé qu'une fois.
 */
export const refreshToken = () => {
  if (pendingRefresh) {
    return pendingRefresh;
  }
  const refresh = window.localStorage.getItem("refresh_token");
  if (!refresh) {
    return Promise.reject(new Error("Pas de refresh token"));
  }
  pendingRefresh = axios
    .post(`${API}/token/refresh`, { refresh_token: refresh }, { headers: authHeaders(null) })
    .then((res) => {
      window.localStorage.setItem("token", res.data.token);
      window.localStorage.setItem("refresh_token", res.data.refresh_token);
      store.dispatch(sessionOperations.tokenRefreshed(res.data.token, res.data.refresh_token));
      return res.data.token;
    })
    .finally(() => {
      pendingRefresh = null;
    });
  return pendingRefresh;
};

/**
 * Appel axios direct à l'API admin, hors pipeline redux (multipart, appels
 * one-shot avec rapport). `path` est relatif à l'API, sans `?apiKey`.
 * Pas de refresh automatique hors redux : un 401 (jeton expiré) déclenche ici
 * un refresh puis un seul nouvel essai.
 */
export const adminRequest = ({ method = "get", path, data, headers = {} }) => {
  const send = (token) =>
    axios({
      method,
      url: `${API}${path}?apiKey=${API_KEY}`,
      data,
      headers: { ...headers, ...authHeaders(token) },
    });

  return send(currentToken()).catch((err) => {
    if (err?.response?.status !== 401) {
      throw err;
    }
    return refreshToken().then(send);
  });
};
